import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Group,
  Image,
  Modal,
  Paper,
  Text,
  ThemeIcon,
  Timeline,
  Tooltip,
  CopyButton,
  ActionIcon,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { openConfirmModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import {
  IResourceComponentsProps,
  useApiUrl,
  useCustomMutation,
  useShow,
} from "@refinedev/core";
import { DateField, Show } from "@refinedev/mantine";
import {
  ReviewRequest,
  ReviewLineItem,
  SOURCE_COLORS,
  SOURCE_LABELS,
} from "entities/review_request";
import { countryCodeToFlag } from "utils/countryFlag";
import {
  IconMail,
  IconUser,
  IconPackage,
  IconCalendar,
  IconSend,
  IconCircleCheck,
  IconAlertCircle,
  IconCalendarEvent,
  IconCopy,
  IconCheck,
  IconWorld,
  IconHash,
  IconFileBarcode,
  IconBell,
  IconCalendarTime,
} from "@tabler/icons";

/* ── Status stepper showing request lifecycle ───────────────── */
const STATUS_ORDER = ["scheduled", "sent"] as const;

function StatusStepper({ status }: { status: string }) {
  const isFailed = status === "failed";
  const activeIndex = STATUS_ORDER.indexOf(status as any);

  const steps = [
    {
      label: "Scheduled",
      icon: <IconCalendarEvent size={14} />,
      color: "blue",
    },
    { label: "Sent", icon: <IconSend size={14} />, color: "green" },
  ];

  if (isFailed) {
    return (
      <Paper
        p="md"
        radius="md"
        sx={(theme) => ({
          backgroundColor:
            theme.colorScheme === "dark"
              ? theme.fn.rgba(theme.colors.red[9], 0.15)
              : theme.colors.red[0],
          border: `1px solid ${
            theme.colorScheme === "dark"
              ? theme.colors.red[8]
              : theme.colors.red[2]
          }`,
        })}
      >
        <Group spacing="sm">
          <ThemeIcon size={32} radius="xl" color="red" variant="light">
            <IconAlertCircle size={18} />
          </ThemeIcon>
          <Box>
            <Text size="sm" weight={700} color="red">
              Failed
            </Text>
            <Text size="xs" color="dimmed">
              This review request could not be delivered
            </Text>
          </Box>
        </Group>
      </Paper>
    );
  }

  return (
    <Timeline active={activeIndex} bulletSize={28} lineWidth={2} color="pink">
      {steps.map((step, i) => (
        <Timeline.Item
          key={step.label}
          bullet={
            <ThemeIcon
              size={28}
              radius="xl"
              color={i <= activeIndex ? step.color : "gray"}
              variant={i <= activeIndex ? "filled" : "light"}
            >
              {i < activeIndex ? <IconCircleCheck size={14} /> : step.icon}
            </ThemeIcon>
          }
          title={
            <Text
              size="xs"
              weight={i <= activeIndex ? 700 : 500}
              color={i <= activeIndex ? undefined : "dimmed"}
            >
              {step.label}
            </Text>
          }
        />
      ))}
    </Timeline>
  );
}

/* ── Inline detail row ──────────────────────────────────────── */
function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Group
      noWrap
      spacing="sm"
      py={8}
      sx={(theme) => ({
        borderBottom: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[5]
            : theme.colors.gray[1]
        }`,
        "&:last-of-type": { borderBottom: "none" },
      })}
    >
      <ThemeIcon size={24} radius="md" variant="light" color="gray">
        {icon}
      </ThemeIcon>
      <Text
        size="xs"
        color="dimmed"
        weight={600}
        sx={{ minWidth: 130, flexShrink: 0 }}
      >
        {label}
      </Text>
      <Box sx={{ flex: 1, textAlign: "right" }}>{children}</Box>
    </Group>
  );
}

/* ── Copyable text value ────────────────────────────────────── */
function CopyableText({ value }: { value: string }) {
  if (!value)
    return (
      <Text size="sm" color="dimmed">
        —
      </Text>
    );

  return (
    <Group spacing={4} position="right" noWrap>
      <Text size="sm" weight={500} sx={{ fontFamily: "monospace" }}>
        {value}
      </Text>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy"} withinPortal>
            <ActionIcon
              size={18}
              variant="subtle"
              color={copied ? "green" : "gray"}
              onClick={copy}
            >
              {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

/* ── Line item card ─────────────────────────────────────────── */
function LineItemCard({ item }: { item: ReviewLineItem }) {
  return (
    <Paper
      p="sm"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${
          theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.gray[2]
        }`,
      })}
    >
      <Group>
        {item.image_url ? (
          <Image
            src={item.image_url}
            width={56}
            height={56}
            fit="contain"
            radius="md"
            sx={(theme) => ({
              border: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[4]
                  : theme.colors.gray[2]
              }`,
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[0],
            })}
          />
        ) : (
          <Box
            sx={(theme) => ({
              width: 56,
              height: 56,
              borderRadius: theme.radius.md,
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[1],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            })}
          >
            <IconPackage size={20} color="gray" />
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }}>
          <Text size="sm" weight={600} lineClamp={1}>
            {item.title || "Untitled item"}
          </Text>
          <Group spacing="xs" mt={4}>
            {item.sku && (
              <Badge variant="outline" color="gray" size="xs" radius="sm">
                SKU: {item.sku}
              </Badge>
            )}
            <Text size="xs" color="dimmed">
              Qty: {item.quantity}
            </Text>
            <Text size="xs" weight={600}>
              {item.price != null ? `¥${item.price.toLocaleString()}` : "—"}
            </Text>
          </Group>
        </Box>
      </Group>
    </Paper>
  );
}

/* ── Main show component ────────────────────────────────────── */
export const ReviewRequestShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<ReviewRequest>();
  const { data, isLoading, refetch } = queryResult;
  const apiUrl = useApiUrl();
  const { mutate: sendEmail, isLoading: isSendingEmail } = useCustomMutation();
  const { mutate: reschedule, isLoading: isRescheduling } = useCustomMutation();
  const [rescheduleOpened, setRescheduleOpened] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [rescheduleTime, setRescheduleTime] = useState<Date | null>(null);

  const record = data?.data;

  const handleReschedule = () => {
    if (!record || !rescheduleDate) return;
    const date = new Date(rescheduleDate);
    if (rescheduleTime) {
      date.setHours(
        rescheduleTime.getHours(),
        rescheduleTime.getMinutes(),
        0,
        0,
      );
    }
    reschedule(
      {
        url: `${apiUrl}/review_requests/${record.id}/reschedule`,
        method: "patch",
        values: { scheduled_for: date.toISOString() },
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Rescheduled",
            message: "Review request has been rescheduled successfully",
            color: "green",
          });
          setRescheduleOpened(false);
          refetch();
        },
        onError: (error: any) => {
          showNotification({
            title: "Failed to Reschedule",
            message:
              error?.response?.data?.error ||
              error?.message ||
              "An error occurred while rescheduling",
            color: "red",
          });
        },
      },
    );
  };

  const handleSendEmail = () => {
    if (!record) return;
    openConfirmModal({
      title: "Send Review Request Email",
      children: (
        <Text size="sm">
          Are you sure you want to send the review request email to{" "}
          <strong>{record.email}</strong> immediately?
        </Text>
      ),
      labels: { confirm: "Send Email", cancel: "Cancel" },
      confirmProps: { color: "pink" },
      onConfirm: () => {
        sendEmail(
          {
            url: `${apiUrl}/review_requests/${record.id}/send_email`,
            method: "post",
            values: {},
          },
          {
            onSuccess: () => {
              showNotification({
                title: "Email Sent",
                message: `Review request email has been sent to ${record.email}`,
                color: "green",
              });
              refetch();
            },
            onError: (error: any) => {
              showNotification({
                title: "Failed to Send Email",
                message:
                  error?.response?.data?.error ||
                  error?.message ||
                  "An error occurred while sending the email",
                color: "red",
              });
            },
          },
        );
      },
    });
  };

  if (!record) return <div />;

  return (
    <Show isLoading={isLoading} canEdit={false}>
      <Grid gutter="lg">
        {/* ── Main column ─────────────────────────────────────── */}
        <Grid.Col span={8}>
          <Flex direction="column" gap="md">
            {/* Customer & contact section */}
            <Paper
              p="lg"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Group spacing="sm" mb="md">
                <ThemeIcon size={28} radius="md" variant="light" color="pink">
                  <IconUser size={16} />
                </ThemeIcon>
                <Text
                  size="sm"
                  weight={700}
                  transform="uppercase"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Customer Information
                </Text>
              </Group>

              <DetailRow icon={<IconMail size={14} />} label="Email">
                <CopyableText value={record.email} />
              </DetailRow>
              <DetailRow icon={<IconUser size={14} />} label="Customer Name">
                <Text size="sm" weight={500}>
                  {record.customer_name || "—"}
                </Text>
              </DetailRow>
              <DetailRow icon={<IconUser size={14} />} label="First Name">
                <Text size="sm" weight={500}>
                  {record.customer_first_name || "—"}
                </Text>
              </DetailRow>
            </Paper>

            {/* Order details section */}
            <Paper
              p="lg"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Group spacing="sm" mb="md">
                <ThemeIcon size={28} radius="md" variant="light" color="grape">
                  <IconPackage size={16} />
                </ThemeIcon>
                <Text
                  size="sm"
                  weight={700}
                  transform="uppercase"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Order Information
                </Text>
              </Group>

              <DetailRow icon={<IconHash size={14} />} label="Order Number">
                <CopyableText value={record.order_number} />
              </DetailRow>
              <DetailRow
                icon={<IconFileBarcode size={14} />}
                label="Remote Order ID"
              >
                <CopyableText value={record.remote_order_id} />
              </DetailRow>
              <DetailRow
                icon={<IconWorld size={14} />}
                label="Shipping Country"
              >
                {record.shipping_country ? (
                  <Group spacing={8} noWrap position="right">
                    <Text size="lg" sx={{ lineHeight: 1 }}>
                      {countryCodeToFlag(record.shipping_country)}
                    </Text>
                    <Text size="sm" weight={600}>
                      {record.shipping_country}
                    </Text>
                  </Group>
                ) : (
                  <Text size="sm" color="dimmed">
                    —
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconHash size={14} />} label="Token">
                <CopyableText value={record.token} />
              </DetailRow>
            </Paper>

            {/* Delivery timing section */}
            <Paper
              p="lg"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Group spacing="sm" mb="md">
                <ThemeIcon size={28} radius="md" variant="light" color="teal">
                  <IconCalendar size={16} />
                </ThemeIcon>
                <Text
                  size="sm"
                  weight={700}
                  transform="uppercase"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Delivery Timeline
                </Text>
              </Group>

              <DetailRow
                icon={<IconCalendarEvent size={14} />}
                label="Scheduled For"
              >
                {record.scheduled_for ? (
                  <DateField
                    value={record.scheduled_for}
                    format="ddd, MMM D YYYY [at] HH:mm"
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    Not scheduled
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconSend size={14} />} label="Sent At">
                {record.sent_at ? (
                  <DateField
                    value={record.sent_at}
                    format="ddd, MMM D YYYY [at] HH:mm"
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    Not yet sent
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconBell size={14} />} label="Reminder Sent At">
                {record.reminder_sent_at ? (
                  <DateField
                    value={record.reminder_sent_at}
                    format="ddd, MMM D YYYY [at] HH:mm"
                  />
                ) : (
                  <Text size="sm" color="dimmed">
                    No reminder sent
                  </Text>
                )}
              </DetailRow>
              <DetailRow icon={<IconCircleCheck size={14} />} label="Reviewed">
                <Badge
                  variant="light"
                  color={record.reviewed ? "green" : "gray"}
                  size="sm"
                  radius="xl"
                >
                  {record.reviewed ? "Completed" : "Awaiting review"}
                </Badge>
              </DetailRow>
            </Paper>

            {/* Review Line Items */}
            {record.review_line_items &&
              record.review_line_items.length > 0 && (
                <Paper
                  p="lg"
                  radius="md"
                  sx={(theme) => ({
                    border: `1px solid ${
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[4]
                        : theme.colors.gray[2]
                    }`,
                  })}
                >
                  <Group spacing="sm" mb="md">
                    <ThemeIcon
                      size={28}
                      radius="md"
                      variant="light"
                      color="orange"
                    >
                      <IconPackage size={16} />
                    </ThemeIcon>
                    <Text
                      size="sm"
                      weight={700}
                      transform="uppercase"
                      sx={{ letterSpacing: 0.5 }}
                    >
                      Line Items
                    </Text>
                    <Badge variant="light" color="gray" size="xs" radius="xl">
                      {record.review_line_items.length}
                    </Badge>
                  </Group>
                  <Flex direction="column" gap="xs">
                    {record.review_line_items.map((item) => (
                      <LineItemCard key={item.id} item={item} />
                    ))}
                  </Flex>
                </Paper>
              )}
          </Flex>
        </Grid.Col>

        {/* ── Sidebar ─────────────────────────────────────────── */}
        <Grid.Col span={4}>
          <Flex
            direction="column"
            gap="md"
            sx={{ position: "sticky", top: 16 }}
          >
            {/* ID card */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
                textAlign: "center",
              })}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={600}
                transform="uppercase"
                sx={{ letterSpacing: 0.5 }}
              >
                Request ID
              </Text>
              <Text
                size="xl"
                weight={800}
                sx={{ fontFamily: "monospace" }}
                mt={4}
              >
                #{record.id}
              </Text>
            </Paper>

            {/* Send Email CTA */}
            <Button
              fullWidth
              size="md"
              leftIcon={<IconSend size={18} />}
              loading={isSendingEmail}
              onClick={handleSendEmail}
              sx={(theme) => ({
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.pink[7]
                    : theme.colors.pink[6],
                "&:hover": {
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.colors.pink[6]
                      : theme.colors.pink[7],
                },
              })}
            >
              Send Email Now
            </Button>

            {/* Reschedule CTA */}
            <Button
              fullWidth
              size="md"
              variant="light"
              color="blue"
              leftIcon={<IconCalendarTime size={18} />}
              loading={isRescheduling}
              onClick={() => {
                setRescheduleDate(
                  record.scheduled_for ? new Date(record.scheduled_for) : null,
                );
                setRescheduleTime(
                  record.scheduled_for ? new Date(record.scheduled_for) : null,
                );
                setRescheduleOpened(true);
              }}
            >
              Reschedule
            </Button>

            {/* Status progress */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={600}
                transform="uppercase"
                sx={{ letterSpacing: 0.5 }}
                mb="md"
              >
                Status
              </Text>
              <StatusStepper status={record.status} />
            </Paper>

            {/* Source */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={600}
                transform="uppercase"
                sx={{ letterSpacing: 0.5 }}
                mb="sm"
              >
                Source
              </Text>
              <Badge
                variant="light"
                color={SOURCE_COLORS[record.source] || "gray"}
                size="lg"
                radius="xl"
                sx={{ fontWeight: 700 }}
              >
                {SOURCE_LABELS[record.source] || record.source}
              </Badge>
            </Paper>

            {/* Timestamps */}
            <Paper
              p="md"
              radius="md"
              sx={(theme) => ({
                border: `1px solid ${
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[4]
                    : theme.colors.gray[2]
                }`,
              })}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={600}
                transform="uppercase"
                sx={{ letterSpacing: 0.5 }}
                mb="sm"
              >
                Timestamps
              </Text>
              <Flex direction="column" gap={8}>
                <Group position="apart">
                  <Text size="xs" color="dimmed">
                    Created
                  </Text>
                  <Text size="xs" weight={500}>
                    <DateField
                      value={record.created_at}
                      format="MMM D, YYYY HH:mm"
                    />
                  </Text>
                </Group>
                <Divider variant="dashed" />
                <Group position="apart">
                  <Text size="xs" color="dimmed">
                    Updated
                  </Text>
                  <Text size="xs" weight={500}>
                    <DateField
                      value={record.updated_at}
                      format="MMM D, YYYY HH:mm"
                    />
                  </Text>
                </Group>
              </Flex>
            </Paper>
          </Flex>
        </Grid.Col>
      </Grid>
      {/* Reschedule Modal */}
      <Modal
        opened={rescheduleOpened}
        onClose={() => setRescheduleOpened(false)}
        title="Reschedule Review Request"
        centered
      >
        <Text size="sm" color="dimmed" mb="md">
          Pick a new date and time for this review request to be sent.
        </Text>
        <Flex gap="sm" mb="lg">
          <DatePicker
            label="Date"
            placeholder="Pick date"
            value={rescheduleDate}
            onChange={setRescheduleDate}
            withinPortal
            sx={{ flex: 1 }}
          />
          <TimeInput
            label="Time"
            value={rescheduleTime}
            onChange={setRescheduleTime}
            sx={{ flex: 1 }}
          />
        </Flex>
        <Group position="right">
          <Button variant="default" onClick={() => setRescheduleOpened(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            loading={isRescheduling}
            disabled={!rescheduleDate}
            onClick={handleReschedule}
          >
            Reschedule
          </Button>
        </Group>
      </Modal>
    </Show>
  );
};
